import { memo, Fragment, useMemo, forwardRef } from 'react'
import { css, jsx } from '@emotion/react'
import { useColorMode } from '@chakra-ui/react'
import { rgba } from 'polished'
import { Input, InputGroup, InputRightElement } from '../chakra/Input'
import { IconButton } from '../chakra/IconButton'
import { Filter } from '../chakra/icons/Filter'
import Div from '../Div'
import useResponsive from '../../utils/hooks/useResponsive'
import { Tooltip } from '../chakra/Tooltip'
import { Text } from '../chakra/Text'
import { Box } from '../chakra/Box'
import theme from '../../constants/theme'
import { FolderDefaultIcon, PublicDefaultIcon } from '../../constants'
import Emoji from '../Emoji'
import { Button } from '../chakra/Button'
import { MenuButton, MenuList, Menu, MenuOptionGroup, MenuDivider } from '../chakra/Menu'
import { MenuItemOption } from '../chakra/Menu/MenuItemOption'
import { CloseRoundedFilled } from '../chakra/icons/CloseRoundedFilled'
import { Skeleton } from '../chakra/Skeleton'
import MaxWidth from '../MaxWidth'
import { analyticsTrack } from '../../utils/analytics'

const styles = {
  container: (padding, colorModeInverse, scrollPassLimit) => css`
    padding: ${padding} 0;
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: ${scrollPassLimit ? `0 3px 3px ${rgba(theme.colors.background[colorModeInverse], 0.02)}` : ''};
    border-bottom: ${scrollPassLimit ? `1px solid ${rgba(theme.colors.background[colorModeInverse], 0.08)}` : ''};
  `,
  maxWidth: padding => css`
    padding: 0 ${padding} !important;
  `,
  headerWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  inputContainer: searchMaxW => css`
    max-width: ${searchMaxW};
    flex: 1;
    display: flex;
    justify-content: flex-end;
    position: relative;
  `,
  menuIcon: padding => css`
    margin-right: ${padding};
  `,
  menuOptions: padding => css`
    margin-left: ${padding};
  `,
  addLinkTooltipOffset: padding => [0, Number(padding.replace('px', ''))],
  searchTooltipOffset: padding => [10, Number(padding.replace('px', '')) + 4],
  emojiIcon: css`
    margin-right: -2px;
  `,
  menuItem: css`
    font-size: 14px;
    padding-top: 0.3rem;
    padding-bottom: 0.3rem;
  `,
  menuContent: css`
    min-width: 12rem;
  `,
  searchModeButton: colorMode => css`
    &:focus {
      box-shadow: 0 0 0 3px ${theme.colors.primary.base} !important;
    }

    ${colorMode === 'light' &&
    `
      border-color: ${theme.colors.border[colorMode]};
      &:hover {
      background: #E2E8F0;
    `}
  `,
  clearInput: css`
    color: ${theme.colors.primary.base};
    position: absolute;
    right: 85px;
  `,
  filterIcon: isOpen => css`
    transform: rotate(${isOpen ? '180deg' : '0deg'});
    transition: transform ${theme.transition.duration.normalSlow};
  `,
}

const canChangeSearch = (searchMode = {}, val) => {
  if (!val) return true

  return Object.values(searchMode).filter(value => value)?.length > 1
}

// const LoadingTitle = () => (
//   <Div flex centerV>
//     <Skeleton height="20px" width="20px" mr="8px" borderRadius="5px" />
//     <Skeleton height="20px" width="130px" mr="10px" borderRadius="5px" />
//   </Div>
// )

const Title = ({ values }) => (
  <Div flex centerV>
    {values.icon && <values.icon boxSize={5} filled />}
    <Text ml={values.icon ? '6px' : 0} fontWeight="medium">
      {values.name} {values.count ? `(${values.count})` : ''}
    </Text>
    {values.rightIcon && <values.rightIcon />}
  </Div>
)

const PublicSearchHeader = forwardRef(
  (
    {
      padding,
      setInputValue,
      inputValue,
      menuSearchOptions,
      viewMode,
      setViewMode,
      sortMode,
      setSortMode,
      sortModeOptions,
      searchMode,
      setSearchMode,
      scrollPassLimit,
      viewModeOptions,
      setLargeDebounceTime,
      folderId,
      userId,
      folderAndCount,
    },
    ref
  ) => {
    const searchMaxW = useResponsive('none', 'none', '380px')
    const showTitleUp = useResponsive(false, false, false, true)
    const { colorMode } = useColorMode()
    const colorModeInverse = (colorMode === 'dark' && 'light') || (colorMode === 'light' && 'dark')
    const isLoadingIds = !folderId && !userId
    const titleValues = useMemo(() => {
      return folderAndCount
        ? {
            icon: folderAndCount?.folder?.icon
              ? () => <Emoji css={styles.emojiIcon}>{folderAndCount?.folder.icon?.metadata?.native}</Emoji>
              : FolderDefaultIcon,
            name: folderAndCount?.folder?.name,
            count: folderAndCount?.count,
            rightIcon: () => (
              <Tooltip label="Public folder" placement="bottom" shouldWrapChildren>
                <PublicDefaultIcon boxSize={5} ml="12px" mb="1px" filled />
              </Tooltip>
            ),
          }
        : {
            icon: () => <span style={{ display: 'none' }} />,
            rightIcon: () => <span style={{ display: 'none' }} />,
            name: '',
            count: 0,
          }
    }, [folderAndCount])

    const TitleComp = <Title values={titleValues} />

    return (
      <div css={styles.container(padding, colorModeInverse, scrollPassLimit)} ref={ref}>
        <MaxWidth css={styles.maxWidth(padding)}>
          <div css={styles.headerWrapper}>
            <div>{showTitleUp && TitleComp}</div>
            <Div flex flexNumber={1} justifyContent="flex-end">
              <div css={styles.inputContainer(searchMaxW)}>
                <InputGroup size="sm">
                  <Input
                    variant="filled"
                    size="sm"
                    placeholder={isLoadingIds ? 'Loading...' : 'Type search...'}
                    value={inputValue}
                    disabled={isLoadingIds}
                    onChange={e => {
                      setLargeDebounceTime(true)
                      const newVal = e.target.value
                      setInputValue(newVal)
                      setTimeout(() => setLargeDebounceTime(false), 350)
                    }}
                    pr="103px"
                  />
                  <InputRightElement width="80px">
                    {inputValue && (
                      <CloseRoundedFilled boxSize={4} onClick={() => setInputValue('')} css={styles.clearInput} />
                    )}
                    <Menu placement="bottom-end" closeOnSelect={false} autoSelect={false}>
                      {() => (
                        <Fragment>
                          <Tooltip
                            label=" Select type of search"
                            placement="bottom-start"
                            offset={styles.searchTooltipOffset(padding)}
                          >
                            <MenuButton
                              as={Button}
                              size="xs"
                              mr="4px"
                              width="76px"
                              variant="outline"
                              textTransform="capitalize"
                              css={styles.searchModeButton(colorMode)}
                            >
                              Search by
                            </MenuButton>
                          </Tooltip>
                          <MenuList css={styles.menuContent}>
                            <MenuOptionGroup title="Search bookmarks">
                              {menuSearchOptions.map(option => (
                                <Tooltip
                                  key={option.value}
                                  label={
                                    option.label === 'Full-body text'
                                      ? 'Returns any bookmark that contain at least one match on the page body'
                                      : null
                                  }
                                  placement="bottom"
                                >
                                  <MenuItemOption
                                    type="radio"
                                    css={styles.menuItem}
                                    value={option.value}
                                    isChecked={!!searchMode[option.value]}
                                    onClick={() =>
                                      canChangeSearch(searchMode, searchMode[option.value]) &&
                                      setSearchMode(prev => {
                                        const value = { ...prev, [option.value]: !searchMode[option.value] }
                                        analyticsTrack(`search_bookmarks_by_public`, {
                                          value,
                                        })
                                        return value
                                      })
                                    }
                                  >
                                    By {option.label}
                                  </MenuItemOption>
                                </Tooltip>
                              ))}
                            </MenuOptionGroup>
                          </MenuList>
                        </Fragment>
                      )}
                    </Menu>
                  </InputRightElement>
                </InputGroup>
              </div>
              <Menu placement="bottom-end" closeOnSelect={false} autoSelect={false}>
                {({ isOpen }) => (
                  <Fragment>
                    <Tooltip
                      label="View & Sort mode"
                      placement="bottom-start"
                      offset={styles.addLinkTooltipOffset(padding)}
                    >
                      <MenuButton
                        as={IconButton}
                        aria-label="Search options"
                        icon={<Filter boxSize={5} css={styles.filterIcon(isOpen)} />}
                        size="sm"
                        colorScheme="primaryButton"
                        gray
                        css={styles.menuOptions(padding)}
                      />
                    </Tooltip>
                    <MenuList css={styles.menuContent}>
                      <MenuOptionGroup title="View mode">
                        {viewModeOptions.map(option => (
                          <MenuItemOption
                            key={option.value}
                            css={styles.menuItem}
                            value={option.value}
                            isChecked={!!viewMode[option.value]}
                            onClick={() =>
                              setViewMode(prev => {
                                const value = { ...prev, [option.value]: !viewMode[option.value] }
                                analyticsTrack('view_mode_change_public', { value })
                                return value
                              })
                            }
                          >
                            {option.label}
                          </MenuItemOption>
                        ))}
                      </MenuOptionGroup>
                      <MenuDivider />
                      <MenuOptionGroup title="Sort mode">
                        {sortModeOptions.map(option => (
                          <Tooltip
                            key={option.value}
                            label={option.default ? 'Default sort mode' : null}
                            placement="top"
                          >
                            <MenuItemOption
                              key={option.value}
                              type="radio"
                              css={styles.menuItem}
                              value={option.value}
                              isChecked={sortMode === option.value}
                              onClick={() => {
                                setSortMode(option.value)
                                analyticsTrack('sort_mode_change_public', { value: option.value })
                              }}
                            >
                              {option.label}
                            </MenuItemOption>
                          </Tooltip>
                        ))}
                      </MenuOptionGroup>
                    </MenuList>
                  </Fragment>
                )}
              </Menu>
            </Div>
          </div>
          {!showTitleUp && (
            <Div center>
              <Box mt="15px" display="inline-block">
                {TitleComp}
              </Box>
            </Div>
          )}
        </MaxWidth>
      </div>
    )
  }
)

export default memo(PublicSearchHeader)
